import React, { useState, useEffect } from 'react';
import {
  Voicemail, Play, Pause, Download, Trash2, Archive,
  Check, Clock, AlertCircle, User, Briefcase, FileText,
  Sparkles, Brain, Wand2, Volume2, X, PhoneCall
} from 'lucide-react';
import { voipService } from '../../services/voipService';
import { formatDateTime } from '../../utils/dateUtils';

const VoicemailList = ({ extension }) => {
  const [voicemails, setVoicemails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('unread'); // all, unread, read
  const [selectedVoicemail, setSelectedVoicemail] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(null);

  useEffect(() => {
    loadVoicemails();
  }, [filter]);

  const loadVoicemails = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter === 'unread') {
        params.unread_only = true;
      }
      const data = await voipService.getVoicemails(params);

      // Apply client-side filter
      let filtered = data;
      if (filter === 'read') {
        filtered = data.filter(vm => vm.is_read);
      }

      setVoicemails(filtered);
    } catch (error) {
      console.error('Error loading voicemails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (voicemailId, isRead) => {
    try {
      await voipService.updateVoicemail(voicemailId, { is_read: isRead });
      setVoicemails(prev => prev.map(vm =>
        vm.id === voicemailId ? { ...vm, is_read: isRead } : vm
      ));
    } catch (error) {
      console.error('Error updating voicemail:', error);
    }
  };

  const handleArchive = async (voicemailId) => {
    try {
      await voipService.archiveVoicemail(voicemailId);
      setVoicemails(prev => prev.filter(vm => vm.id !== voicemailId));
      if (selectedVoicemail?.id === voicemailId) {
        setSelectedVoicemail(null);
      }
    } catch (error) {
      console.error('Error archiving voicemail:', error);
    }
  };

  const handleVoicemailClick = async (voicemail) => {
    setSelectedVoicemail(voicemail);

    // Mark as read when opened
    if (!voicemail.is_read) {
      await handleMarkRead(voicemail.id, true);
    }
  };

  const handleGenerateAISummary = async (voicemailId) => {
    setGeneratingAI(voicemailId);
    // Simulate AI generation - would call actual API
    setTimeout(() => {
      setGeneratingAI(null);
    }, 2000);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyColors = {
      high: 'bg-gradient-to-r from-red-500/20 to-blue-500/20 text-red-300 border border-red-500/30',
      normal: 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 border border-cyan-500/30',
      low: 'bg-slate-700/50 text-slate-400 border border-slate-600/30'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${urgencyColors[urgency] || urgencyColors.normal}`}>
        {urgency}
      </span>
    );
  };

  const unreadCount = voicemails.filter(vm => !vm.is_read).length;
  const totalCount = voicemails.length;
  const aiAnalyzedCount = voicemails.filter(vm => vm.astraea_summary).length;

  return (
    <div className="h-full flex bg-gradient-to-br from-slate-900 via-slate-900/30 to-slate-900 rounded-xl overflow-hidden">
      {/* Voicemail List */}
      <div className="flex-1 flex flex-col">
        {/* Quick Stats */}
        <div className="p-4 grid grid-cols-3 gap-3">
          <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-cyan-500/20">
            <div className="text-2xl font-bold text-white">{totalCount}</div>
            <div className="text-xs text-cyan-400">Total</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-cyan-500/20">
            <div className="text-2xl font-bold text-blue-400">{unreadCount}</div>
            <div className="text-xs text-cyan-400">Unread</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur rounded-xl p-3 border border-cyan-500/20">
            <div className="flex items-center gap-1">
              <Brain className="w-4 h-4 text-cyan-400" />
              <span className="text-2xl font-bold text-cyan-400">{aiAnalyzedCount}</span>
            </div>
            <div className="text-xs text-cyan-400">AI Analyzed</div>
          </div>
        </div>

        {/* Header with Filters */}
        <div className="px-4 pb-4">
          <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-cyan-500/20 p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Voicemail className="w-5 h-5 text-cyan-400" />
                Voicemails
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-sm rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </h2>
              <button
                onClick={loadVoicemails}
                className="px-4 py-2 text-sm text-cyan-300 hover:text-cyan-100 font-medium bg-cyan-500/10 hover:bg-cyan-500/20 rounded-lg transition-all"
              >
                Refresh
              </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('unread')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === 'unread'
                    ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                    : 'bg-slate-700/50 text-cyan-300 hover:bg-slate-700'
                }`}
              >
                Unread ({unreadCount})
              </button>
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === 'all'
                    ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                    : 'bg-slate-700/50 text-cyan-300 hover:bg-slate-700'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('read')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === 'read'
                    ? 'bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                    : 'bg-slate-700/50 text-cyan-300 hover:bg-slate-700'
                }`}
              >
                Read
              </button>
            </div>
          </div>
        </div>

        {/* Voicemail Items */}
        <div className="flex-1 overflow-auto px-4 pb-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-cyan-500 border-t-transparent"></div>
            </div>
          ) : voicemails.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Voicemail className="w-8 h-8 text-cyan-400" />
                </div>
                <p className="text-cyan-300">
                  {filter === 'unread' ? 'No unread voicemails' : 'No voicemails found'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {voicemails.map((voicemail) => (
                <div
                  key={voicemail.id}
                  onClick={() => handleVoicemailClick(voicemail)}
                  className={`p-4 rounded-xl cursor-pointer transition-all ${
                    selectedVoicemail?.id === voicemail.id
                      ? 'bg-gradient-to-r from-cyan-600/30 to-blue-600/30 border border-cyan-500/50 shadow-lg shadow-cyan-500/10'
                      : voicemail.is_read
                        ? 'bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50 hover:border-cyan-500/30'
                        : 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 hover:border-cyan-500/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        voicemail.is_read
                          ? 'bg-slate-700/50'
                          : 'bg-gradient-to-br from-cyan-500 to-blue-500 shadow-lg shadow-cyan-500/30'
                      }`}>
                        <Voicemail className={`w-5 h-5 ${voicemail.is_read ? 'text-slate-400' : 'text-white'}`} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className={`text-sm font-medium truncate ${
                          voicemail.is_read ? 'text-slate-300' : 'text-white'
                        }`}>
                          {voicemail.from_name || voicemail.from_number || 'Unknown'}
                        </p>
                        {getUrgencyBadge(voicemail.astraea_urgency)}
                        {voicemail.astraea_summary && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full text-xs text-cyan-300 border border-cyan-500/30">
                            <Sparkles className="w-3 h-3" />
                            AI
                          </span>
                        )}
                        {!voicemail.is_read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                        )}
                      </div>

                      <p className="text-sm text-slate-400 mb-2">
                        {voicemail.from_number}
                      </p>

                      {/* Astraea Summary */}
                      {voicemail.astraea_summary && (
                        <p className="text-sm text-cyan-200 mb-2 line-clamp-2 bg-cyan-500/10 rounded-lg p-2 border border-cyan-500/20">
                          {voicemail.astraea_summary}
                        </p>
                      )}

                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(voicemail.created_at, { year: undefined })}
                        </span>
                        {voicemail.duration_seconds && (
                          <span className="flex items-center gap-1">
                            <Volume2 className="w-3 h-3" />
                            {formatDuration(voicemail.duration_seconds)}
                          </span>
                        )}
                        {voicemail.contact_id && (
                          <span className="flex items-center gap-1 text-cyan-400">
                            <User className="w-3 h-3" />
                            Contact
                          </span>
                        )}
                        {voicemail.case_id && (
                          <span className="flex items-center gap-1 text-blue-400">
                            <Briefcase className="w-3 h-3" />
                            Case
                          </span>
                        )}
                      </div>

                      {/* Astraea Suggested Action */}
                      {voicemail.astraea_suggested_action && (
                        <div className="mt-2 flex items-start gap-2 p-2 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-lg text-xs border border-cyan-500/20">
                          <Wand2 className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span className="text-cyan-200">{voicemail.astraea_suggested_action}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0 flex flex-col gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkRead(voicemail.id, !voicemail.is_read);
                        }}
                        className="p-2 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-all"
                        title={voicemail.is_read ? 'Mark as unread' : 'Mark as read'}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchive(voicemail.id);
                        }}
                        className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Archive"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Voicemail Details Panel */}
      {selectedVoicemail && (
        <div className="w-96 bg-slate-800/50 backdrop-blur border-l border-cyan-500/20 overflow-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Voicemail Details</h3>
              <button
                onClick={() => setSelectedVoicemail(null)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Caller Info */}
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-cyan-500/30">
                  {(selectedVoicemail.from_name || selectedVoicemail.from_number || 'U')[0].toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-white">
                    {selectedVoicemail.from_name || 'Unknown Caller'}
                  </div>
                  <div className="text-sm text-cyan-300">
                    {selectedVoicemail.from_number}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-700/30 rounded-lg p-3 border border-cyan-500/10">
                  <div className="text-cyan-400 mb-1 text-xs">Received</div>
                  <div className="font-medium text-white">
                    {formatDateTime(selectedVoicemail.created_at)}
                  </div>
                </div>
                <div className="bg-slate-700/30 rounded-lg p-3 border border-cyan-500/10">
                  <div className="text-cyan-400 mb-1 text-xs">Duration</div>
                  <div className="font-medium text-white">{formatDuration(selectedVoicemail.duration_seconds)}</div>
                </div>
                <div className="bg-slate-700/30 rounded-lg p-3 border border-cyan-500/10">
                  <div className="text-cyan-400 mb-1 text-xs">Urgency</div>
                  <div>{getUrgencyBadge(selectedVoicemail.astraea_urgency)}</div>
                </div>
                <div className="bg-slate-700/30 rounded-lg p-3 border border-cyan-500/10">
                  <div className="text-cyan-400 mb-1 text-xs">Status</div>
                  <div>
                    {selectedVoicemail.is_read ? (
                      <span className="text-slate-400">Read</span>
                    ) : (
                      <span className="text-blue-400 font-medium">Unread</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Audio Player */}
            <div className="mb-6">
              <div className="text-sm font-medium text-cyan-300 mb-2">Voicemail Recording</div>
              <div className="bg-slate-700/30 rounded-xl p-4 border border-cyan-500/20">
                <audio controls className="w-full" style={{ filter: 'invert(1) hue-rotate(180deg)' }}>
                  <source src={`/api/voip/voicemails/${selectedVoicemail.id}/audio`} type="audio/wav" />
                </audio>
              </div>
              <button className="mt-3 w-full text-sm text-cyan-300 hover:text-white flex items-center justify-center gap-2 py-2 bg-slate-700/30 hover:bg-slate-700/50 rounded-lg transition-all border border-cyan-500/20">
                <Download className="w-4 h-4" />
                Download Recording
              </button>
            </div>

            {/* Transcription */}
            {selectedVoicemail.transcription_text && (
              <div className="mb-6">
                <div className="text-sm font-medium text-cyan-300 mb-2">Transcription</div>
                <div className="bg-slate-700/30 rounded-xl p-4 text-sm text-slate-200 border border-cyan-500/20">
                  {selectedVoicemail.transcription_text}
                </div>
              </div>
            )}

            {/* Astraea AI Analysis */}
            {selectedVoicemail.astraea_summary ? (
              <div className="mb-6">
                <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium text-white">Astraea AI Summary</span>
                  </div>
                  <p className="text-sm text-cyan-200 mb-3">
                    {selectedVoicemail.astraea_summary}
                  </p>
                  {selectedVoicemail.astraea_suggested_action && (
                    <div className="pt-3 border-t border-cyan-500/30">
                      <div className="text-xs font-medium text-blue-300 mb-1 flex items-center gap-1">
                        <Wand2 className="w-3 h-3" />
                        Suggested Action
                      </div>
                      <p className="text-sm text-cyan-200">
                        {selectedVoicemail.astraea_suggested_action}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <button
                  onClick={() => handleGenerateAISummary(selectedVoicemail.id)}
                  disabled={generatingAI === selectedVoicemail.id}
                  className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl font-medium hover:from-cyan-500 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {generatingAI === selectedVoicemail.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Brain className="w-4 h-4" />
                      Generate AI Summary
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Linked Records */}
            {(selectedVoicemail.contact_id || selectedVoicemail.case_id) && (
              <div className="mb-6">
                <div className="text-sm font-medium text-cyan-300 mb-2">Linked Records</div>
                <div className="space-y-2">
                  {selectedVoicemail.contact_id && (
                    <a href={`/contacts/${selectedVoicemail.contact_id}`}
                      className="flex items-center gap-2 p-3 bg-slate-700/30 hover:bg-slate-700/50 text-cyan-300 hover:text-white rounded-xl transition-all border border-cyan-500/20"
                    >
                      <User className="w-4 h-4" />
                      <span className="text-sm font-medium">View Contact</span>
                    </a>
                  )}
                  {selectedVoicemail.case_id && (
                    <a href={`/cases/${selectedVoicemail.case_id}`}
                      className="flex items-center gap-2 p-3 bg-slate-700/30 hover:bg-slate-700/50 text-blue-300 hover:text-white rounded-xl transition-all border border-blue-500/20"
                    >
                      <Briefcase className="w-4 h-4" />
                      <span className="text-sm font-medium">View Case</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2">
              <button
                onClick={() => handleMarkRead(selectedVoicemail.id, !selectedVoicemail.is_read)}
                className="w-full px-4 py-3 bg-slate-700/30 hover:bg-slate-700/50 text-cyan-300 hover:text-white rounded-xl transition-all text-sm font-medium border border-cyan-500/20"
              >
                Mark as {selectedVoicemail.is_read ? 'Unread' : 'Read'}
              </button>
              <button className="w-full px-4 py-3 bg-gradient-to-r from-green-600/80 to-emerald-600/80 hover:from-green-500 hover:to-emerald-500 text-white rounded-xl transition-all text-sm font-medium flex items-center justify-center gap-2 shadow-lg shadow-green-500/20">
                <PhoneCall className="w-4 h-4" />
                Call Back
              </button>
              <button
                onClick={() => handleArchive(selectedVoicemail.id)}
                className="w-full px-4 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-xl transition-all text-sm font-medium border border-red-500/20"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoicemailList;
